import { CalendarDate } from '@internationalized/date';
import {
  AddressType,
  FreightTerm,
  PaymentTerm,
  SalesOrderType,
  WeightDisplayUnit,
  enumAddressType,
  enumSalesOrderType,
  enumUnit,
  enumWeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { validateLineItems } from '../_components/salesOrders/helpers/validateLineItems';
import { salesOrderCreateAtom } from '../_graphql/mutationSalesOrderCreate';
import { salesOrderGetNextDocumentNumberAtom } from '../_graphql/mutationSalesOrderGetNextDocumentNumber';
import { salesOrderUpdateAtom } from '../_graphql/mutationSalesOrderUpdate';
import { accountsByIdsQueryAtom } from '../_graphql/queryAccountsByIds';
import { salesOrderDetailQueryAtom } from '../_graphql/querySalesOrderDetail';
import { BookingRecord } from '../types/booking';
import { SalesOrderLineItemRecord } from '../types/salesOrder';
import { calculateTotalPrice } from '../utils/money';

export type SalesOrderFormState = {
  id: string | null;
  // Contract Info
  type: SalesOrderType;
  facilityId: string | null;
  salesOrderStartDate: CalendarDate | null;
  salesOrderEndDate: CalendarDate | null;
  salesOrderNumber: string;
  accountRepresentativeId: string | null;
  counterpartyPurchaseOrderNumber: string;
  counterpartyReferenceNumber: string;
  doNotShip: boolean;

  // Customer Information
  customerName: string;
  customerId: string | null;
  contactId: string | null;
  shipToLocationId: string | null;
  billToLocationId: string | null;
  sameAsShip: boolean;

  // Addresses
  shippingAddress: {
    addressType: AddressType;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    isMailingAddress: boolean;
  } | null;
  billingAddress: {
    addressType: AddressType;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    isMailingAddress: boolean;
  } | null;

  // Payment Info
  paymentCurrency: string | null;
  paymentTerm: PaymentTerm | null;
  freightTerm: FreightTerm | null;

  // Materials
  lineItems: SalesOrderLineItemRecord[];

  // Other Info
  internalNotes: string;
  externalNotes: string;
  completionNotes: string;
  customAttributes: Record<string, unknown>;
  weightDisplayUnit: WeightDisplayUnit | null;

  // Form state
  isSubmitting: boolean;
  errors: Record<string, string>;
  bookings?: Pick<BookingRecord, 'id' | 'bookingNumber'>[];
};

const initialSalesOrderFormState: SalesOrderFormState = {
  id: null,
  bookings: [],
  // Contract Info
  type: enumSalesOrderType.International,
  facilityId: null,
  salesOrderStartDate: null,
  salesOrderEndDate: null,
  salesOrderNumber: '',
  accountRepresentativeId: null,
  counterpartyPurchaseOrderNumber: '',
  counterpartyReferenceNumber: '',
  doNotShip: false,

  // Customer Information
  customerName: '',
  customerId: null,
  contactId: null,
  shipToLocationId: null,
  billToLocationId: null,
  sameAsShip: true,

  // Addresses
  shippingAddress: {
    addressType: enumAddressType.Standard,
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    isMailingAddress: true,
  },
  billingAddress: {
    addressType: enumAddressType.Standard,
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    isMailingAddress: true,
  },

  // Payment Info
  paymentCurrency: null,
  paymentTerm: null,
  freightTerm: null,

  // Materials
  lineItems: [],

  // Other Info
  internalNotes: '',
  externalNotes: '',
  completionNotes: '',
  customAttributes: {},
  weightDisplayUnit: enumWeightDisplayUnit.PriceUnit,

  // Form state
  isSubmitting: false,
  errors: {},
};

// Persistent storage for draft sales order forms by ID
export const $draftSalesOrderForms = atomWithStorage<
  Map<string, SalesOrderFormState>
>('sales-order-draft-forms', new Map(), {
  // Custom storage with Map serialization
  getItem: (key) => {
    const stored = localStorage.getItem(key);
    if (!stored) return new Map();
    try {
      const parsed = JSON.parse(stored);

      // Reconstruct CalendarDate objects from plain objects
      const reconstructedEntries: [string, SalesOrderFormState][] = (
        Object.entries(parsed) as [string, SalesOrderFormState][]
      ).map(([id, form]) => [
        id,
        {
          ...form,
          salesOrderStartDate: form.salesOrderStartDate
            ? new CalendarDate(
                form.salesOrderStartDate.year,
                form.salesOrderStartDate.month,
                form.salesOrderStartDate.day,
              )
            : null,
          salesOrderEndDate: form.salesOrderEndDate
            ? new CalendarDate(
                form.salesOrderEndDate.year,
                form.salesOrderEndDate.month,
                form.salesOrderEndDate.day,
              )
            : null,
        },
      ]);

      return new Map(reconstructedEntries);
    } catch (error) {
      console.error('Failed to parse localStorage sales order forms:', error);
      return new Map();
    }
  },
  setItem: (key, value) => {
    // Serialize with date handling
    const obj = Object.fromEntries(
      Array.from(value.entries()).map(([id, form]) => [
        id,
        {
          ...form,
          // Convert CalendarDate objects to plain objects for JSON serialization
          salesOrderStartDate: form.salesOrderStartDate
            ? {
                calendar: form.salesOrderStartDate.calendar,
                era: form.salesOrderStartDate.era,
                year: form.salesOrderStartDate.year,
                month: form.salesOrderStartDate.month,
                day: form.salesOrderStartDate.day,
              }
            : null,
          salesOrderEndDate: form.salesOrderEndDate
            ? {
                calendar: form.salesOrderEndDate.calendar,
                era: form.salesOrderEndDate.era,
                year: form.salesOrderEndDate.year,
                month: form.salesOrderEndDate.month,
                day: form.salesOrderEndDate.day,
              }
            : null,
        },
      ]),
    );

    const jsonString = JSON.stringify(obj);
    localStorage.setItem(key, jsonString);
  },
  removeItem: (key) => localStorage.removeItem(key),
});

export const $currentStep = atom<number>(1);
export const $setCurrentStep = atom(null, (get, set, step: number) => {
  set($currentStep, step);
});

export const $resetCurrentStep = atom(null, (get, set) => {
  set($currentStep, 1);
});
// Read-only computed atom for current form state
export const $salesOrderForm = atom<SalesOrderFormState>((get) => {
  const drafts = get($draftSalesOrderForms);
  const currentId = get($currentDraftId);

  if (currentId && drafts.has(currentId)) {
    return drafts.get(currentId) ?? initialSalesOrderFormState;
  }

  return initialSalesOrderFormState;
});

// Write-only atom for updating form state
export const $updateSalesOrderFormState = atom(
  null,
  (get, set, updates: Partial<SalesOrderFormState>) => {
    const currentForm = get($salesOrderForm);
    const newForm = { ...currentForm, ...updates };

    // Store in persistent storage if has ID
    if (newForm.id) {
      const drafts = get($draftSalesOrderForms);
      const newDrafts = new Map(drafts);
      newDrafts.set(newForm.id, newForm);
      set($draftSalesOrderForms, newDrafts);
    }

    // Update current draft ID if changed
    if (updates.id !== undefined) {
      set($currentDraftId, updates.id);
    }
  },
);

// Track current draft ID being edited - internal storage
const $currentDraftIdInternal = atom<string | null>(null);

// Track current draft ID being edited - public interface with logging
export const $currentDraftId = atom(
  (get) => get($currentDraftIdInternal),
  (get, set, newValue: string | null) => set($currentDraftIdInternal, newValue),
);

// Helper atoms for updating specific parts of the form
export const $updateSalesOrderForm = atom(
  null,
  (get, set, updates: Partial<SalesOrderFormState>) => {
    set($updateSalesOrderFormState, updates);
  },
);

// Set entire form state atom
export const $setSalesOrderForm = atom(
  null,
  (get, set, formState: Partial<SalesOrderFormState>) => {
    set($updateSalesOrderFormState, formState);
  },
);

export const $setSalesOrderId = atom(null, (get, set, id: string | null) => {
  set($updateSalesOrderFormState, { id });
});

// Reset form atom
export const $resetSalesOrderForm = atom(null, (get, set) => {
  set($currentDraftId, null);
  set($currentStep, 1);
  // This will trigger the computed atom to return initialSalesOrderFormState
});

/**
 * Required fields for a non-draft sales order based on
 * SalesOrderWizardStepContractInfo isRequired props.
 */
export const SALES_ORDER_REQUIRED_FIELDS = [
  'salesOrderNumber',
  'facilityId',
  'salesOrderStartDate',
  'customerId',
  'shipToLocationId',
  'billToLocationId',
  'paymentCurrency',
  'paymentTerm',
  'freightTerm',
] as const;

export type SalesOrderRequiredField =
  (typeof SALES_ORDER_REQUIRED_FIELDS)[number];

type FieldValidator = (form: SalesOrderFormState) => boolean;

const fieldValidators: Record<SalesOrderRequiredField, FieldValidator> = {
  salesOrderNumber: (form) => !form.salesOrderNumber.trim(),
  facilityId: (form) => !form.facilityId,
  salesOrderStartDate: (form) => !form.salesOrderStartDate,
  customerId: (form) => !form.customerId,
  shipToLocationId: (form) => !form.shipToLocationId,
  billToLocationId: (form) => !form.billToLocationId && !form.sameAsShip,
  paymentCurrency: (form) => !form.paymentCurrency,
  paymentTerm: (form) => !form.paymentTerm,
  freightTerm: (form) => !form.freightTerm,
};

const fieldErrorMessages: Record<SalesOrderRequiredField, string> = {
  salesOrderNumber: 'Sales order number is required',
  facilityId: 'Facility is required',
  salesOrderStartDate: 'Start date is required',
  customerId: 'Customer is required',
  shipToLocationId: 'Ship to location is required',
  billToLocationId: 'Bill to location is required',
  paymentCurrency: 'Payment currency is required',
  paymentTerm: 'Payment term is required',
  freightTerm: 'Freight term is required',
};

const getMissingRequiredFields = (
  form: SalesOrderFormState,
): SalesOrderRequiredField[] =>
  SALES_ORDER_REQUIRED_FIELDS.filter((field) => fieldValidators[field](form));

// 🟠🟠🟠 ACTION - Validation atom for full form
export const $validateSalesOrderForm = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($salesOrderForm);

    const missingFields = getMissingRequiredFields(form);
    const requiredErrors = missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldErrorMessages[field],
      }),
      {} as Record<string, string>,
    );

    let errors = { ...requiredErrors };

    if (get($currentStep) === 2) {
      const lineItemErrors = validateLineItems(form.lineItems);
      errors = { ...errors, ...lineItemErrors };
    }

    set($updateSalesOrderFormState, { errors });
    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

// 🟢🟢🟢 COMPUTED DATA - Missing Required Fields
export const $salesOrderMissingRequiredFields = atom((get) => {
  const form = get($salesOrderForm);
  return getMissingRequiredFields(form);
});

// 🟢🟢🟢 COMPUTED DATA - Is Missing Required
export const $salesOrderIsMissingRequired = atom(
  (get) => get($salesOrderMissingRequiredFields).length > 0,
);

// 🟠🟠🟠 ACTION - Validation atom for draft mode (only requires sales order number)
export const $validateSalesOrderFormDraft = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($salesOrderForm);
    let errors: Record<string, string> = {};

    // Only validate sales order number and account id for draft
    if (!form.salesOrderNumber.trim()) {
      errors.salesOrderNumber = 'Sales order number is required';
    }

    if (!form.customerId) {
      errors.customerId = 'Customer is required';
    }

    if (get($currentStep) === 2) {
      const lineItemErrors = validateLineItems(form.lineItems);
      errors = { ...errors, ...lineItemErrors };
      set($updateSalesOrderFormState, { errors });
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  },
);
// Helper atoms for line items
export const $addLineItem = atom(null, (get, set) => {
  const form = get($salesOrderForm);
  const newItem: SalesOrderLineItemRecord = {
    id: `temp-${Date.now()}-${Math.random()}`,
    unit: enumUnit.Pounds,
    quantity: null,
    unitPrice: null,
    totalPrice: null,
    itemId: null,
  };

  set($updateSalesOrderFormState, {
    lineItems: [...form.lineItems, newItem],
  });
});

export const $updateLineItem = atom(
  null,
  (
    get,
    set,
    {
      index,
      updates,
    }: { index: number; updates: Partial<SalesOrderLineItemRecord> },
  ) => {
    const form = get($salesOrderForm);
    const updatedItems = [...form.lineItems];
    const currentItem = updatedItems[index];

    // Apply the updates
    const updatedItem = { ...currentItem, ...updates };

    // Calculate totalPrice if quantity or unitPrice changed
    if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
      updatedItem.totalPrice = calculateTotalPrice(
        updatedItem.unitPrice,
        updatedItem.quantity,
      );
    }

    updatedItems[index] = updatedItem;

    set($updateSalesOrderFormState, {
      lineItems: updatedItems,
    });
  },
);

export const $removeLineItem = atom(null, (get, set, index: number) => {
  const form = get($salesOrderForm);
  const updatedItems = form.lineItems.filter((_, i) => i !== index);

  set($updateSalesOrderFormState, {
    lineItems: updatedItems,
  });
});

// Computed atom for form validity
export const $salesOrderFormIsValid = atom((get) => {
  const form = get($salesOrderForm);
  return Object.keys(form.errors).length === 0;
});

// 🟢🟢🟢 REMOTE DATA - Existing Sales Order
export const $existingSalesOrder = salesOrderDetailQueryAtom((get) => ({
  variables: {
    id: get($salesOrderForm).id ?? '',
  },
  skip:
    !get($salesOrderForm).id || get($salesOrderForm)?.id?.startsWith('temp-'),
}));

// 🟢🟢🟢 REMOTE DATA - Accounts by ID
export const $accountsByIdQueryLazy = accountsByIdsQueryAtom(() => ({
  variables: {} as never,
  lazy: true,
}));

// 🟠🟠🟠 ACTION -> CREATE sales order
export const $createSalesOrder = salesOrderCreateAtom();

// 🟠🟠🟠 ACTION -> UPDATE sales order
export const $updateSalesOrder = salesOrderUpdateAtom();

// 🟠🟠🟠 ACTION -> GET NEXT DOCUMENT NUMBER
export const $nextDocumentNumber = salesOrderGetNextDocumentNumberAtom();
